/**
 * The teacher's assessment of one lesson, as the student sees it in the course player.
 *
 *   useNodeAssessments({ api, nodeId })   → { latest, history, count, loading, reload }
 *   <ResultBadge …/>                      the chip beside the lesson title
 *   <NodeResultsTab …/>                   every earlier result for the same lesson
 *
 * `latest` is the most recent TEACHER assessment — that is the mark the student is shown.
 * `history` carries the parent's records and the LMS's own quiz marking too, so the
 * earlier results read as the full story of that topic rather than a filtered one.
 */
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { Target, ChalkboardTeacher, UsersThree, Robot, CaretRight } from '@phosphor-icons/react';

export const ROLE_META = {
  instructor: { label: 'Teacher', icon: ChalkboardTeacher, tone: 'bg-sky-100 text-sky-800' },
  parent: { label: 'Parent', icon: UsersThree, tone: 'bg-violet-100 text-violet-800' },
  system: { label: 'LMS quiz', icon: Robot, tone: 'bg-emerald-100 text-emerald-800' },
};

export function accuracyTone(value, dark = false) {
  if (value === null || value === undefined) return dark ? 'text-white/50' : 'text-muted-foreground';
  if (value >= 80) return dark ? 'text-emerald-300' : 'text-emerald-700';
  if (value >= 50) return dark ? 'text-amber-300' : 'text-amber-700';
  return dark ? 'text-red-300' : 'text-red-700';
}

/**
 * The DAY the topic was covered, not the moment the teacher typed the record — a mark
 * back-filled on Monday for Friday's lesson belongs to Friday.
 */
const when = (record) => {
  const raw = record?.date || record?.created_at;
  if (!raw) return '';
  const d = new Date(String(raw).length <= 10 ? `${raw}T00:00:00` : raw);
  return Number.isNaN(d.getTime())
    ? String(raw).slice(0, 10)
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Assessments recorded against one course node, for the signed-in student. */
export function useNodeAssessments({ api, nodeId, studentId, enabled = true }) {
  const [data, setData] = useState({ latest: null, history: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled || !nodeId) { setData({ latest: null, history: [], count: 0 }); return; }
    setLoading(true);
    try {
      const q = new URLSearchParams({ node_id: nodeId });
      if (studentId) q.set('student_id', studentId);
      const r = await api.get(`/diary/node-assessments?${q.toString()}`);
      setData(r.data || { latest: null, history: [], count: 0 });
    } catch {
      // A student with no diary yet is the normal case, not an error worth shouting about.
      setData({ latest: null, history: [], count: 0 });
    } finally {
      setLoading(false);
    }
  }, [api, nodeId, studentId, enabled]);

  useEffect(() => { reload(); }, [reload]);
  return { ...data, loading, reload };
}

/**
 * The chip beside the lesson title. Shows the latest teacher mark; clicking it opens the
 * earlier results for the same lesson.
 */
export function ResultBadge({ latest, count, dark = false, onClick }) {
  if (!latest) return null;
  const more = Math.max(0, (count || 1) - 1);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="node-result-badge"
      title={`Teacher's assessment on ${when(latest)}${more ? ` — ${more} earlier result${more === 1 ? '' : 's'}` : ''}. Click to see the earlier results.`}
      className={`inline-flex items-center gap-1.5 rounded-full pl-2 pr-1.5 h-7 text-[12px] font-medium border transition-colors align-middle
        ${dark ? 'border-white/20 bg-white/10 hover:bg-white/20 text-white' : 'border-border bg-white hover:bg-muted/60'}`}
    >
      <Target className="w-3.5 h-3.5 opacity-70" />
      <span className={`tabular-nums font-semibold ${accuracyTone(latest.accuracy, dark)}`} data-testid="node-result-accuracy">
        {latest.accuracy === null || latest.accuracy === undefined ? '—' : `${latest.accuracy}%`}
      </span>
      <span className={dark ? 'text-white/60' : 'text-muted-foreground'}>
        {when(latest)}
      </span>
      {more > 0 && (
        <span className={`rounded-full px-1.5 h-[18px] inline-flex items-center text-[10px] ${dark ? 'bg-white/15' : 'bg-muted'}`} data-testid="node-result-more">
          +{more}
        </span>
      )}
      <CaretRight className="w-3 h-3 opacity-50" />
    </button>
  );
}

/** One row in the history. */
function ResultRow({ record, dark }) {
  const meta = ROLE_META[record.assessor_role] || ROLE_META.instructor;
  const Icon = meta.icon;
  const counts = record.method === 'questions';
  return (
    <li className={`rounded-md border p-2.5 space-y-1 ${dark ? 'border-white/10 bg-white/5' : 'border-border'}`}
      data-testid={`node-result-${record.assessor_role}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase ${meta.tone}`}>
          <Icon className="w-3 h-3" weight="fill" />{meta.label}
        </span>
        <span className="text-[12px] font-medium">{record.assessor_name}</span>
        <span className={`ml-auto text-[15px] font-semibold tabular-nums ${accuracyTone(record.accuracy, dark)}`}>
          {record.accuracy === null || record.accuracy === undefined ? '—' : `${record.accuracy}%`}
        </span>
      </div>
      <p className={`text-[11px] ${dark ? 'text-white/50' : 'text-muted-foreground'}`}>
        {when(record)}
        {' · '}
        {counts
          ? `${record.correct ?? 0} correct · ${record.partial ?? 0} partly · ${record.incorrect ?? 0} wrong${record.questions_asked ? ` of ${record.questions_asked}` : ''}`
          : record.method === 'quiz'
            ? `marked by the LMS${record.score !== undefined && record.score !== null ? ` · ${record.score}/${record.max_score}` : ''}`
            : 'understanding score'}
      </p>
      {record.remarks && <p className="text-[12px] whitespace-pre-wrap">{record.remarks}</p>}
    </li>
  );
}

/** Every result recorded for this lesson, newest first. */
export function NodeResultsTab({ latest, history = [], loading, dark = false }) {
  if (loading && !history.length) {
    return <p className={`p-3 text-[13px] ${dark ? 'text-white/50' : 'text-muted-foreground'}`}>Loading results…</p>;
  }
  if (!history.length) {
    return (
      <div className="p-4 text-center" data-testid="node-results-empty">
        <Target className={`w-7 h-7 mx-auto mb-2 ${dark ? 'text-white/25' : 'text-muted-foreground/40'}`} />
        <p className={`text-[13px] ${dark ? 'text-white/50' : 'text-muted-foreground'}`}>
          No assessment of this lesson yet. Your teacher's marks appear here.
        </p>
      </div>
    );
  }
  const earlier = history.filter((r) => r.id !== latest?.id);
  return (
    <div className="p-3 space-y-3" data-testid="node-results-tab">
      {latest && (
        <section className="space-y-1.5">
          <h4 className={`text-[11px] font-semibold uppercase tracking-wide ${dark ? 'text-white/50' : 'text-muted-foreground'}`}>
            Latest from your teacher
          </h4>
          <ul className="space-y-2"><ResultRow record={latest} dark={dark} /></ul>
        </section>
      )}
      {earlier.length > 0 && (
        <section className="space-y-1.5">
          <h4 className={`text-[11px] font-semibold uppercase tracking-wide ${dark ? 'text-white/50' : 'text-muted-foreground'}`}>
            Earlier results ({earlier.length})
          </h4>
          <ul className="space-y-2" data-testid="node-results-earlier">
            {earlier.map((r) => <ResultRow key={r.id} record={r} dark={dark} />)}
          </ul>
        </section>
      )}
    </div>
  );
}
